// US-BUCKET-16 -- guardian directly moves money from one of a child's
// buckets to another. Instant -- no approval queue. The form shows:
//   - source bucket dropdown (pre-selected if passed in)
//   - target bucket dropdown (excludes source)
//   - amount with live balance validation
//   - optional note

import { type BucketEntry, guardianBucketTransfer } from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { AmountPad } from '../components/AmountPad';

type Props = {
  sessionId: string;
  /** All active buckets for the child. Caller should pre-filter to one child. */
  buckets: BucketEntry[];
  currency: string;
  /** Optional pre-selected source bucket. */
  initialSourceBucketId?: number;
  onSaved: (sourceBucketId: number, targetBucketId: number, amountMinorUnits: number) => void;
  onCancel: () => void;
};

function formatMoney(minorUnits: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(
      minorUnits / 100,
    );
  } catch {
    return `${currency} ${(minorUnits / 100).toFixed(2)}`;
  }
}

function parseAmount(value: string): number | null {
  const clean = value.replace(/[^0-9.]/g, '');
  const f = Number.parseFloat(clean);
  if (Number.isNaN(f) || f <= 0) return null;
  return Math.round(f * 100);
}

export const GuardianBucketTransferScreen: Component<Props> = (props) => {
  const activeBuckets = createMemo(() => props.buckets.filter((b) => b.balanceMinorUnits >= 0));

  const defaultSource =
    props.initialSourceBucketId?.toString() ?? activeBuckets()[0]?.bucketId.toString() ?? '';

  const [sourceBucketIdStr, setSourceBucketIdStr] = createSignal(defaultSource);
  const [targetBucketIdStr, setTargetBucketIdStr] = createSignal('');
  const [amountStr, setAmountStr] = createSignal('');
  const [note, setNote] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const sourceBucket = createMemo(() =>
    activeBuckets().find((b) => b.bucketId.toString() === sourceBucketIdStr()),
  );

  const availableTargets = createMemo(() =>
    activeBuckets().filter((b) => b.bucketId.toString() !== sourceBucketIdStr()),
  );

  const targetBucket = createMemo(() =>
    availableTargets().find((b) => b.bucketId.toString() === targetBucketIdStr()),
  );

  const amount = createMemo(() => parseAmount(amountStr()));

  const amountError = createMemo(() => {
    const a = amount();
    const src = sourceBucket();
    if (a === null || !src) return null;
    if (a > src.balanceMinorUnits) {
      return `Amount exceeds ${src.bucketName} balance of ${formatMoney(src.balanceMinorUnits, props.currency)}`;
    }
    return null;
  });

  const canSubmit = createMemo(() => {
    const a = amount();
    return (
      a !== null &&
      a > 0 &&
      sourceBucketIdStr() !== '' &&
      targetBucketIdStr() !== '' &&
      sourceBucketIdStr() !== targetBucketIdStr() &&
      amountError() === null
    );
  });

  async function handleSubmit() {
    const a = amount();
    const srcId = Number(sourceBucketIdStr());
    const tgtId = Number(targetBucketIdStr());
    if (!a || !srcId || !tgtId) return;

    setBusy(true);
    setError(null);
    try {
      const result = await guardianBucketTransfer({
        sessionId: props.sessionId,
        sourceBucketId: srcId,
        targetBucketId: tgtId,
        amountMinorUnits: a,
        ...(note().trim() ? { note: note().trim() } : {}),
      });
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      props.onSaved(srcId, tgtId, a);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionScreen
      eyebrow="Posts immediately — no approval"
      title="Move money"
      onBack={props.onCancel}
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          onClick={handleSubmit}
          disabled={!canSubmit() || busy()}
        >
          {busy() ? 'Transferring…' : 'Transfer'}
        </button>
      }
    >
      <div class="action-form">
        <label class="field-group" for="transfer-source">
          <span class="field-label">From bucket</span>
          <select
            id="transfer-source"
            class="input"
            value={sourceBucketIdStr()}
            onChange={(e) => {
              setSourceBucketIdStr(e.currentTarget.value);
              setTargetBucketIdStr('');
              setError(null);
            }}
            disabled={busy()}
          >
            <option value="">-- select --</option>
            <For each={activeBuckets()}>
              {(b) => (
                <option value={b.bucketId.toString()}>
                  {b.bucketName} ({formatMoney(b.balanceMinorUnits, props.currency)})
                </option>
              )}
            </For>
          </select>
        </label>

        <label class="field-group" for="transfer-target">
          <span class="field-label">To bucket</span>
          <select
            id="transfer-target"
            class="input"
            value={targetBucketIdStr()}
            onChange={(e) => {
              setTargetBucketIdStr(e.currentTarget.value);
              setError(null);
            }}
            disabled={busy() || !sourceBucketIdStr()}
          >
            <option value="">-- select --</option>
            <For each={availableTargets()}>
              {(b) => (
                <option value={b.bucketId.toString()}>
                  {b.bucketName} ({formatMoney(b.balanceMinorUnits, props.currency)})
                </option>
              )}
            </For>
          </select>
        </label>

        <AmountPad
          id="transfer-amount"
          label="Amount"
          value={amountStr()}
          currencyCode={props.currency}
          hint={
            sourceBucket()
              ? `Available: ${formatMoney(sourceBucket()?.balanceMinorUnits ?? 0, props.currency)}`
              : undefined
          }
          disabled={busy()}
          onChange={(value) => {
            setAmountStr(value);
            setError(null);
          }}
        />
        <Show when={amountError()}>
          <span class="field-error">{amountError()}</span>
        </Show>

        <Show when={sourceBucket() && targetBucket() && amount()}>
          <div class="guardian-transfer-preview">
            <span class="guardian-transfer-preview-row">
              <span>{sourceBucket()?.bucketName}</span>
              <span class="num">
                {formatMoney(sourceBucket()?.balanceMinorUnits ?? 0, props.currency)}
                {' -> '}
                {formatMoney(
                  (sourceBucket()?.balanceMinorUnits ?? 0) - (amount() ?? 0),
                  props.currency,
                )}
              </span>
            </span>
            <span class="guardian-transfer-preview-arrow">
              ↓ {formatMoney(amount() ?? 0, props.currency)}
            </span>
            <span class="guardian-transfer-preview-row">
              <span>{targetBucket()?.bucketName}</span>
              <span class="num">
                {formatMoney(targetBucket()?.balanceMinorUnits ?? 0, props.currency)}
                {' -> '}
                {formatMoney(
                  (targetBucket()?.balanceMinorUnits ?? 0) + (amount() ?? 0),
                  props.currency,
                )}
              </span>
            </span>
          </div>
        </Show>

        <label class="field-group" for="transfer-note">
          <span class="field-label">Note (optional)</span>
          <input
            id="transfer-note"
            type="text"
            class="input"
            placeholder="Reason for transfer"
            value={note()}
            onInput={(e) => setNote(e.currentTarget.value)}
            disabled={busy()}
          />
        </label>

        <Show when={error()}>
          <div class="form-error" role="alert">
            {error()}
          </div>
        </Show>
      </div>
    </ActionScreen>
  );
};
